import { useState } from 'react'

import { Primary13Regular, Flex } from '@sequencehq/core-components'
import {
  DateDurationPopover,
  InputSelectControl,
  Popover
} from '@sequencehq/core-components'
import { add, format } from '@sequencehq/utils/dates'
import { uniq } from 'lodash'
import { match, P } from 'ts-pattern'
import type { Option } from '@sequencehq/core-components'
import { DATE_FORMAT_API, DATE_FORMAT_DISPLAY } from 'Invoices/constants'
import type { DueDateBasis, DueDatePolicy } from 'Invoices/types'
import { DEFAULT_NET_PRESETS } from 'SettingsV2/settings.constants'
import { CustomPaymentTermsModal } from 'components/PaymentTerms/CustomPaymentTermsModal'

const CUSTOM_DATE = 'CUSTOM_DATE'
const CUSTOM_TERMS = 'CUSTOM_TERMS'

type Props = {
  /**
   * basis
   * Where the merchant's due dates are counted from
   */
  basis: DueDateBasis
  /**
   * defaultDueDays
   * Default number of days that an invoice is due for this merchant
   */
  defaultDueDays: number
  /**
   * fallbackTermDays
   * Term days in effect while there is no policy — a legacy invoice's own
   * terms, else the merchant default. Never pre-selected: the policy is unset.
   */
  fallbackTermDays?: number
  /**
   * value
   * Currently selected due date policy
   */
  value?: DueDatePolicy
  /**
   * onChange
   * Change the invoice due date policy
   */
  onChange: (newValue: DueDatePolicy) => void
  /**
   * width
   * Width of the select control
   */
  width?: string
  disabled?: boolean
  /**
   * placeholder
   * Overrides the prompt shown while no term is selected — e.g. a due date
   * already on the invoice that the policy's terms cannot explain.
   */
  placeholder?: string
  'data-testid'?: string
  /**
   * If true, automatically opens the dropdown menu on mount
   */
  initiallyOpen?: boolean
}

export const InvoiceDueDateField = ({
  basis,
  defaultDueDays,
  fallbackTermDays,
  value,
  onChange: onChangeProp,
  width,
  disabled = false,
  placeholder: placeholderProp,
  'data-testid': dataTestid,
  initiallyOpen = false
}: Props) => {
  const [datePickerPopoverOpen, setDatePickerPopoverOpen] =
    useState<boolean>(false)
  const [termsModalOpen, setTermsModalOpen] = useState<boolean>(false)

  const selection = match(value)
    .with({ type: 'PAYMENT_TERMS' }, ({ paymentTermDays }) => ({
      selectedValue: String(paymentTermDays),
      // Keeps a non-standard term, e.g. Net 21, selectable
      extraPresets: [paymentTermDays]
    }))
    .with({ type: 'MANUAL' }, () => ({
      selectedValue: CUSTOM_DATE,
      extraPresets: []
    }))
    .with(undefined, () => ({
      selectedValue: '',
      // Keeps a legacy invoice's own term selectable, without selecting it
      extraPresets: fallbackTermDays === undefined ? [] : [fallbackTermDays]
    }))
    .exhaustive()

  const overrideSelectedLabel = match(value)
    .with({ type: 'MANUAL' }, ({ date }) =>
      format(new Date(date), DATE_FORMAT_DISPLAY)
    )
    .otherwise(() => undefined)

  const presetLabels = (preset: number) =>
    match(basis)
      .with({ calculatedFrom: 'ACCOUNTING_DATE' }, ({ accountingDate }) => ({
        label: format(
          add(accountingDate, { days: preset }),
          DATE_FORMAT_DISPLAY
        ),
        secondaryLabel: `Net ${preset}`
      }))
      // Nothing to count the days from until the backend issues the invoice
      .with({ calculatedFrom: 'ISSUE_DATE' }, () => ({
        label: `Net ${preset}`
      }))
      .exhaustive()

  const presets = uniq([
    ...DEFAULT_NET_PRESETS,
    defaultDueDays,
    ...selection.extraPresets
  ]).sort((a, b) => a - b)

  const options: Option[] = [
    ...presets.map((preset, index) => ({
      ...presetLabels(preset),
      value: String(preset),
      dividerAfter: index === presets.length - 1
    })),
    {
      label: 'Custom terms',
      value: CUSTOM_TERMS
    },
    {
      label: 'Custom date',
      value: CUSTOM_DATE
    }
  ]

  const calendarDate = match({ basis, value })
    .with(
      { value: { type: 'MANUAL' } },
      ({ value: { date } }) => new Date(date)
    )
    // No anchor to count from, so open on today, the earliest plausible issue date
    .with({ basis: { calculatedFrom: 'ISSUE_DATE' } }, () => new Date())
    .with(
      {
        basis: { calculatedFrom: 'ACCOUNTING_DATE' },
        value: { type: 'PAYMENT_TERMS' }
      },
      ({ basis: { accountingDate }, value: { paymentTermDays } }) =>
        add(accountingDate, { days: paymentTermDays })
    )
    // No policy means the backend applies the terms already in effect on issue
    .with(
      { basis: { calculatedFrom: 'ACCOUNTING_DATE' }, value: P.nullish },
      ({ basis: { accountingDate } }) =>
        add(accountingDate, { days: fallbackTermDays ?? defaultDueDays })
    )
    .exhaustive()

  const placeholder =
    placeholderProp ??
    match(basis)
      .with({ calculatedFrom: 'ACCOUNTING_DATE' }, () => 'Select due date')
      .with({ calculatedFrom: 'ISSUE_DATE' }, () => 'Select payment terms')
      .exhaustive()

  const onChange = (selectedValue: string) => {
    if (selectedValue === CUSTOM_TERMS) {
      setTermsModalOpen(true)
      return
    }

    if (selectedValue === CUSTOM_DATE) {
      setDatePickerPopoverOpen(true)
      return
    }

    onChangeProp({
      type: 'PAYMENT_TERMS',
      paymentTermDays: Number(selectedValue)
    })
  }

  return (
    <>
      <Popover.Root
        variant="dateRange"
        isOpen={datePickerPopoverOpen}
        placement="bottom-start"
        onClose={() => setDatePickerPopoverOpen(false)}
        autoFocus
        returnFocusOnClose={false}
        closeOnBlur
        isLazy
        lazyBehavior="unmount"
      >
        <Popover.Anchor>
          <Flex
            width={width}
            tabIndex={-1}
            position="relative"
            transition="border-color 0.2s ease-in-out"
            isolation="auto"
            flexDirection="column"
          >
            <InputSelectControl
              value={selection.selectedValue}
              options={options}
              onChange={onChange}
              triggerStyle={{ width, ...Primary13Regular }}
              overrideSelectedLabel={overrideSelectedLabel}
              placeholder={placeholder}
              justifyLabels="space-between"
              disabled={disabled}
              data-testid={dataTestid}
              initiallyOpen={initiallyOpen}
            />
          </Flex>
        </Popover.Anchor>

        {datePickerPopoverOpen && (
          <Popover.Content>
            <Popover.Body>
              <DateDurationPopover
                data-testid={dataTestid && `${dataTestid}.calendar`}
                selectedDate={calendarDate}
                onDateSelected={newDate => {
                  onChangeProp({
                    type: 'MANUAL',
                    date: format(newDate, DATE_FORMAT_API)
                  })
                  setDatePickerPopoverOpen(false)
                }}
              />
            </Popover.Body>
          </Popover.Content>
        )}
      </Popover.Root>

      <CustomPaymentTermsModal
        isOpen={termsModalOpen}
        setIsOpen={setTermsModalOpen}
        onSubmit={paymentTermDays =>
          onChangeProp({ type: 'PAYMENT_TERMS', paymentTermDays })
        }
      />
    </>
  )
}
